import { PublicClientApplication } from '@azure/msal-browser'
import type { Configuration, AccountInfo } from '@azure/msal-browser'

// MSAL configuration
const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID || 'common'}`,
    redirectUri: import.meta.env.VITE_REDIRECT_URI || window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage', // This configures where your cache is stored
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
}

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig)

// Login request configuration
const loginRequest = {
  scopes: ['User.Read'],
  prompt: 'select_account',
}

// Graph API configuration
const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
}

export class AuthService {
  private static instance: AuthService
  private msalInstance: PublicClientApplication

  private constructor() {
    this.msalInstance = msalInstance
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService()
    }
    return AuthService.instance
  }

  // Initialize MSAL
  public async initialize(): Promise<void> {
    await this.msalInstance.initialize()
  }

  // Login with Microsoft
  public async loginWithMicrosoft(): Promise<AccountInfo | null> {
    try {
      // Check if we're in Teams context by looking for Teams SDK or URL patterns
      const isInTeams = this.isInTeamsContext()

      // console.log('AuthService - Teams context detected:', isInTeams)

      if (isInTeams) {
        // Use redirect for Teams context
        // console.log('AuthService - Using redirect authentication for Teams')
        await this.msalInstance.loginRedirect(loginRequest)
        // Note: This will redirect, so we won't return here
        return null
      } else {
        // Use popup for regular browser context
        // console.log('AuthService - Using popup authentication for regular browser')
        const response = await this.msalInstance.loginPopup(loginRequest)
        return response.account
      }
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  // Check if we're in Teams context
  private isInTeamsContext(): boolean {
    if (typeof window === 'undefined') return false

    // Check for Teams SDK
    const hasTeamsSDK =
      (window as unknown as { microsoftTeams?: unknown }).microsoftTeams !== undefined

    // Check for Teams URL patterns
    const isTeamsURL =
      window.location.href.includes('teams.microsoft.com') ||
      window.location.href.includes('teams.live.com') ||
      window.location.href.includes('teams.office.com')

    // Check user agent for Teams
    const isTeamsUserAgent = navigator.userAgent.includes('Teams')

    // console.log('Teams context check:', {
    //   hasTeamsSDK,
    //   isTeamsURL,
    //   isTeamsUserAgent,
    //   userAgent: navigator.userAgent,
    //   url: window.location.href,
    // })

    return hasTeamsSDK || isTeamsURL || isTeamsUserAgent
  }

  // Login with redirect (alternative to popup)
  public async loginWithRedirect(): Promise<void> {
    try {
      await this.msalInstance.loginRedirect(loginRequest)
    } catch (error) {
      console.error('Login redirect failed:', error)
      throw error
    }
  }

  // Get current account
  public getCurrentAccount(): AccountInfo | null {
    const accounts = this.msalInstance.getAllAccounts()
    return accounts.length > 0 ? accounts[0] : null
  }

  // Get access token
  public async getAccessToken(): Promise<string | null> {
    try {
      const account = this.getCurrentAccount()
      if (!account) {
        throw new Error('No account found')
      }

      const response = await this.msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      })

      return response.accessToken
    } catch (error) {
      console.error('Token acquisition failed:', error)
      return null
    }
  }

  // Get user profile from Microsoft Graph
  public async getUserProfile(): Promise<{
    displayName: string
    mail: string
    userPrincipalName: string
  }> {
    try {
      const token = await this.getAccessToken()
      if (!token) {
        throw new Error('No access token available')
      }

      const response = await fetch(graphConfig.graphMeEndpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch user profile')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to get user profile:', error)
      throw error
    }
  }

  // Logout
  public async logout(): Promise<void> {
    try {
      // Check if we're in Teams context
      const isInTeams = this.isInTeamsContext()

      const account = this.getCurrentAccount()
      if (account) {
        if (isInTeams) {
          // Use redirect for Teams context
          await this.msalInstance.logoutRedirect({
            account: account,
          })
        } else {
          // Use popup for regular browser context
          await this.msalInstance.logoutPopup({
            account: account,
          })
        }
      } else {
        await this.msalInstance.logoutRedirect()
      }
    } catch (error) {
      console.error('Logout failed:', error)
      throw error
    }
  }

  // Check if user is authenticated
  public isAuthenticated(): boolean {
    return this.getCurrentAccount() !== null
  }

  // Handle redirect response (call this after redirect login)
  public async handleRedirectResponse(): Promise<AccountInfo | null> {
    try {
      const response = await this.msalInstance.handleRedirectPromise()
      return response?.account || null
    } catch (error) {
      console.error('Handle redirect response failed:', error)
      return null
    }
  }
}

export default AuthService.getInstance()
